import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { ChangeEvent } from "react";

export type ToolTipPosition = "bottom" | "left" | "right" | "top";

export interface RadioOption {
  label: string;
  value: string;
  disabled?: boolean;
  tooltipAsBubble?: boolean;
  tooltipPosition?: ToolTipPosition;
  tooltip?: React.ReactNode;
}

interface Props {
  baseId: string;
  testIdPrefix?: string;
  value: string;
  options: RadioOption[];
  onChange?: (value: string) => void;
  disabled?: boolean;
  tooltipAsBubble?: boolean;
  tooltip?: string;
  renderOptionContent?: (optionValue: string, index: number) => React.ReactNode;
}

export const RadioOptionsInput: React.FC<Props> = ({
  options,
  onChange,
  baseId,
  testIdPrefix,
  value,
  disabled,
  renderOptionContent,
}) => {
  const handleChange = (_event: ChangeEvent, value: string) => {
    onChange?.(value);
  };

  return (
    <RadioGroup id={baseId} value={value} onChange={handleChange}>
      {options.map((option, index) => {
        const content = (
          <FormControlLabel
            key={index}
            value={option.value}
            label={<Typography variant="body1">{option.label}</Typography>}
            control={
              <Radio
                id={`${baseId}-${option.value}`}
                data-testid={
                  testIdPrefix &&
                  `${testIdPrefix}-${option.value.toLowerCase()}-radio`
                }
                disabled={option.disabled}
                checked={value === option.value}
                color="primary"
              />
            }
            disabled={disabled}
          />
        );
        if (!option.tooltip) {
          return (
            <React.Fragment key={index}>
              {content}
              {renderOptionContent?.(option.value, index)}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            <Tooltip
              title={
                <Typography variant="subtitle2" fontWeight={100}>
                  {option.tooltip}
                </Typography>
              }
              placement="right"
              arrow={option.tooltipAsBubble}
              slotProps={{
                arrow: {
                  sx: {
                    backgroundColor: "background.paper",
                    color: "grey.300",
                  },
                },
                tooltip: {
                  sx: {
                    backgroundColor: "background.paper",
                    color: "grey.500",
                    border: "1px solid",
                    borderColor: "grey.300",
                  },
                },
              }}
            >
              {content}
            </Tooltip>
            {renderOptionContent?.(option.value, index)}
          </React.Fragment>
        );
      })}
    </RadioGroup>
  );
};
